import React from "react";
import { Button, Form, FormGroup, Label, Alert, Input, Spinner } from "reactstrap";
import axios from "axios";
import {
  API_URL_proyectos,
  API_URL_productos,
  API_URL_sedes,
  API_URL, // clientes
  API_URL_partners,
  API_URL_lideres_profit,
} from "../../constants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Select, { createFilter } from "react-select";
import { API_URL_clasificacion_productos } from "../../constants";
import { getAuthHeaders } from "../../utils/auth";

// Dataset offline (solo UI)
import { Country, City } from "country-state-city";
import { cityOverrides } from "./countryCityMap";

// Fusiona el catálogo de country-state-city con un override manual por país
// para cubrir faltantes (ej. Colima, Manzanillo en México).
const buildCityList = (iso2, countryName) => {
  if (!iso2) return [];
  const fromLib = City.getCitiesOfCountry(iso2).map((ct) => ct.name);
  const fromOverride = cityOverrides[countryName] || [];
  return Array.from(new Set([...fromLib, ...fromOverride]))
    .map((name) => ({ name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

class NewProyectoForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // ==== FIELDS that exist in Django model (and we WILL send) ====
      pk: 0,
      name: "",
      status: "",
      cliente: "",
      producto: "",
      modalidad: "",
      inicio_date: null,       // Date
      terminacion_date: null,  // Date
      moneda: "",
      lider: "",
      // Clasificación de producto (5 niveles, ids del catálogo)
      cp_producto: null,
      cp_categoria: null,
      cp_subproducto: null,
      cp_modalidad: null,
      cp_tipo: null,
      clasificacionList: [],

      // ==== UI-only (NO se envían al backend) ====
      sede: "",               // parent Sede id (UI)
      sedeSubcatId: "",       // <-- NEW: selected subcategory id (WILL be sent)
      pais: "",
      pais_code: "",
      ciudad: "",
      countries: [],
      availableCities: [],

      // Lists from API
      clientesList: [],
      productosList: [],
      sedesList: [],          // <-- will include subcategories (embed)
      partnersList: [],

      // Errors
      errorMessage: "",
      errorMessageFecha: "",
      validationErrors: {},
      isSubmitting: false,
    };
  }

  componentDidMount() {
    // Países (ordenados) para UI
    const countries = Country.getAllCountries()
      .map((c) => ({ name: c.name, isoCode: c.isoCode }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Si edita, precargar
    if (this.props.proyecto) {
      const {
        pk,
        name,
        status,
        cliente,
        producto,
        modalidad,
        inicio_date,
        terminacion_date,
        moneda,
        // UI-only si venían en el prop
        pais,
        sede, // if you were already passing sede id in props, we keep it
        ciudad,
        // opcional si el backend devuelve el subcat:
        sede_subcategory,
        sede_subcategory_id,
      } = this.props.proyecto;

      const inicio = inicio_date ? new Date(inicio_date) : null;
      const termina = terminacion_date ? new Date(terminacion_date) : null;

      const matchedCountry = pais
        ? countries.find(
            (c) => c.name.trim().toLowerCase() === String(pais).trim().toLowerCase()
          )
        : null;
      const iso2 = matchedCountry ? matchedCountry.isoCode : "";
      const availableCities = buildCityList(iso2, matchedCountry ? matchedCountry.name : "");

      this.setState({
        pk: pk || 0,
        name: name || "",
        status: status || "",
        cliente: cliente || "",
        producto: producto || "",
        modalidad: modalidad || "",
        inicio_date: inicio,
        terminacion_date: termina,
        moneda: moneda || "",
        lider: this.props.proyecto.lider || "",
        cp_producto: this.props.proyecto.cp_producto || null,
        cp_categoria: this.props.proyecto.cp_categoria || null,
        cp_subproducto: this.props.proyecto.cp_subproducto || null,
        cp_modalidad: this.props.proyecto.cp_modalidad || null,
        cp_tipo: this.props.proyecto.cp_tipo || null,

        // UI-only
        sede: sede || "", // keep what you were passing before
        sedeSubcatId:
          // be resilient to several shapes
          (sede_subcategory && sede_subcategory.id) ||
          sede_subcategory_id ||
          sede_subcategory ||
          "",
        pais: pais || "",
        pais_code: iso2,
        ciudad: ciudad || "",
        countries,
        availableCities,
      });
    } else {
      this.setState({ countries });
    }

    // Fetch de listas
    const headers = getAuthHeaders();

    axios
      .get(API_URL_productos, { headers })
      .then((r) => this.setState({ productosList: r.data }))
      .catch((error) => {
        console.error("Error fetching productos:", error);
      });
    axios
      .get(`${API_URL_clasificacion_productos}?activos=1`, { headers })
      .then((r) => this.setState({ clasificacionList: Array.isArray(r.data) ? r.data : [] }))
      .catch((error) => {
        console.error("Error fetching clasificación de productos:", error);
      });
    // IMPORTANT: fetch sedes WITH subcategories embedded
    axios
      .get(`${API_URL_sedes}?embed=subcategories`, { headers })
      .then((r) => this.setState({ sedesList: r.data }))
      .catch((error) => {
        console.error("Error fetching sedes:", error);
      });
    axios
      .get(API_URL, { headers })
      .then((r) => {
        console.log("Clientes fetched:", r.data);
        this.setState({ clientesList: r.data });
      })
      .catch((error) => {
        console.error("Error fetching clientes:", error);
        console.error("Error details:", error.response?.data || error.message);
      });
    axios
      .get(API_URL_partners, { headers })
      .then((r) => this.setState({ partnersList: r.data }))
      .catch((error) => {
        console.error("Error fetching partners:", error);
      });
  }

  // Text inputs
  onChange = (e) => this.setState({ [e.target.name]: e.target.value });

  // Date helpers
  handleDateChange = (date) => this.setState({ inicio_date: date });
  handleDateChangeTermina = (date) => this.setState({ terminacion_date: date });

  // DateTime -> ISO para DRF
  toIsoDateTime = (d) => {
    if (!d) return null;
    return d.toISOString();
  };

  // País/Ciudad (UI only)
  handleChangePaisSelect = (opt) => {
    const selectedCountryName = opt ? opt.value : "";
    const match = this.state.countries.find((c) => c.name === selectedCountryName);
    const iso2 = match ? match.isoCode : "";

    const availableCities = buildCityList(iso2, selectedCountryName);

    this.setState({
      pais: selectedCountryName,
      pais_code: iso2,
      ciudad: "",
      availableCities,
    });
  };

  handleChangeCiudadSelect = (opt) => this.setState({ ciudad: opt ? opt.value : "" });

  // ----- Clasificación de producto (listas dependientes) -----
  // Subproducto, Modalidad y Tipo cuelgan de la Categoría elegida, o del
  // Producto cuando éste no tiene categorías.
  cpNodos = (nivel, parentId) =>
    (this.state.clasificacionList || [])
      .filter((n) => n.nivel === nivel && (parentId === undefined || n.parent === parentId))
      .sort((a, b) => (a.orden - b.orden) || a.nombre.localeCompare(b.nombre));

  cpOpciones = (nodos) => {
    const conGrupo = nodos.some((n) => n.grupo);
    if (!conGrupo) return nodos.map((n) => ({ value: n.pk, label: n.nombre }));
    const grupos = new Map();
    nodos.forEach((n) => {
      const g = n.grupo || "Otros";
      if (!grupos.has(g)) grupos.set(g, []);
      grupos.get(g).push({ value: n.pk, label: n.nombre });
    });
    return Array.from(grupos.entries()).map(([label, options]) => ({ label, options }));
  };

  cpBuscar = (opciones, value) => {
    for (const o of opciones) {
      if (o.options) {
        const hit = o.options.find((x) => x.value === value);
        if (hit) return hit;
      } else if (o.value === value) {
        return o;
      }
    }
    return null;
  };

  onChangeCp = (campo, opt) => {
    const value = opt ? opt.value : null;
    const next = { [campo]: value, validationErrors: { ...this.state.validationErrors, [campo]: '' } };
    if (campo === "cp_producto") Object.assign(next, { cp_categoria: null, cp_subproducto: null, cp_modalidad: null, cp_tipo: null });
    if (campo === "cp_categoria") Object.assign(next, { cp_subproducto: null, cp_modalidad: null, cp_tipo: null });
    this.setState(next);
  };

  // Nodos elegidos y qué niveles aplican para el producto/categoría actual.
  cpResumen = () => {
    const { cp_producto, cp_categoria, cp_subproducto, cp_modalidad, cp_tipo, clasificacionList } = this.state;
    const nodo = (pk) => (clasificacionList || []).find((n) => n.pk === pk) || null;
    const categorias = cp_producto ? this.cpNodos(2, cp_producto) : [];
    const base = cp_categoria || (cp_producto && categorias.length === 0 ? cp_producto : null);
    return {
      producto: nodo(cp_producto), categoria: nodo(cp_categoria), subproducto: nodo(cp_subproducto),
      modalidad: nodo(cp_modalidad), tipo: nodo(cp_tipo),
      requiereCategoria: categorias.length > 0,
      requiereSub: !!base && this.cpNodos(3, base).length > 0,
      requiereMod: !!base && this.cpNodos(4, base).length > 0,
      requiereTipo: !!base && this.cpNodos(5, base).length > 0,
    };
  };

  // Los campos Producto y Modalidad anteriores siguen alimentando exportaciones
  // y pantallas viejas; se derivan de la clasificación nueva al guardar.
  cpCamposAnteriores = () => {
    const cp = this.cpResumen();
    if (!cp.producto) return { producto: this.state.producto || "", modalidad: this.state.modalidad || "" };
    const producto = cp.subproducto?.nombre || cp.producto.nombre;
    const abierto = cp.tipo ? (cp.tipo.nombre === "Privado" ? "Cerrado" : "Abierto") : null;
    const conTipo = (base) => (abierto ? `${base} - ${abierto}` : base);
    let modalidad;
    switch (cp.producto.nombre) {
      case "Proyectos": modalidad = "Proyecto de implementación"; break;
      case "Coaching": modalidad = "Coaching"; break;
      case "Tecnología": modalidad = "Licencias"; break;
      default: {
        const m = cp.modalidad?.nombre;
        if (m === "Virtual en vivo") modalidad = conTipo("Curso Virtual en vivo");
        else if (m === "Presencial") modalidad = conTipo("Curso Presencial");
        else if (m === "Híbrido") modalidad = conTipo("Curso Híbrido presencial + Virtual");
        else if (m === "Online") modalidad = conTipo("Curso Online autoguiado");
        else modalidad = m || cp.producto.nombre;
      }
    }
    return { producto, modalidad };
  };

  // Payload: SOLO los campos del modelo que sí queremos mandar
  buildProyectoPayload = () => {
    const {
      name,
      status,
      cliente,
      inicio_date,
      terminacion_date,
      moneda,
      lider,           // sigue siendo admitido por el modelo; lo dejamos null
      cp_producto, cp_categoria, cp_subproducto, cp_modalidad, cp_tipo,
      sedeSubcatId,    // <-- NEW: this WILL be sent
      pais,        
      ciudad,      
      sede,  
    } = this.state;

    const payload = {
      name: (name || "").trim(),
      status: status || "",
      cliente: cliente || "",
      ...this.cpCamposAnteriores(),
      inicio_date: this.toIsoDateTime(inicio_date),
      terminacion_date: this.toIsoDateTime(terminacion_date),
      moneda: moneda || "",
      lider: lider || null,
      cp_producto: cp_producto || null,
      cp_categoria: cp_categoria || null,
      cp_subproducto: cp_subproducto || null,
      cp_modalidad: cp_modalidad || null,
      cp_tipo: cp_tipo || null,

      // ⬇️ NEW: send the FK your backend expects
      sede_subcategory: sedeSubcatId || null,
      pais: pais || "",      // ✅ Add this
      ciudad: ciudad || "",  // ✅ Add this
      sede: sede || null,    // send parent sede if needed
    };

    return payload;
  };

  // VALIDATION
  validateForm = () => {
    const errors = {};
    const { name, status, moneda } = this.state;

    if (!name || name.trim() === '') {
      errors.name = 'Por favor ingresa el nombre del proyecto';
    }

    if (!status || status.trim() === '') {
      errors.status = 'Por favor selecciona el status';
    }

    // Clasificación de producto (obligatoria desde la fase 2; los campos
    // Producto / Modalidad anteriores se derivan de ella).
    const cp = this.cpResumen();
    if (!cp.producto) errors.cp_producto = 'Por favor selecciona el producto';
    else {
      if (cp.requiereCategoria && !cp.categoria) errors.cp_categoria = 'Por favor selecciona la categoría';
      if (cp.requiereSub && !cp.subproducto) errors.cp_subproducto = 'Por favor selecciona el subproducto';
      if (cp.requiereMod && !cp.modalidad) errors.cp_modalidad = 'Por favor selecciona la modalidad';
      if (cp.requiereTipo && !cp.tipo) errors.cp_tipo = 'Por favor indica si es público o privado';
    }

    if (!moneda || moneda.trim() === '') {
      errors.moneda = 'Por favor selecciona una moneda';
    }

    this.setState({ validationErrors: errors });
    return Object.keys(errors).length === 0;
  };

  // CREATE
  createProyecto = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!this.validateForm()) {
      this.setState({ errorMessage: 'Por favor completa todos los campos obligatorios marcados con *' });
      // Focus first invalid field
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid-custom, .react-select-invalid');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = firstError.querySelector('input, select');
          if (input) input.focus();
        }
      }, 100);
      return;
    }

    const { inicio_date, terminacion_date } = this.state;
    if (inicio_date && terminacion_date && inicio_date > terminacion_date) {
      this.setState({
        errorMessageFecha: "La fecha de terminación debe ser igual o posterior a la fecha de inicio.",
      });
      return;
    }

    this.setState({ isSubmitting: true, errorMessage: '', errorMessageFecha: '' });

    const headers = getAuthHeaders();
    const payload = this.buildProyectoPayload();

    try {
      await axios.post(API_URL_proyectos, payload, { headers });

      // Auto-create Profit_lider record if leader doesn't have one yet
      if (payload.lider) {
        try {
          const { data: existingProfits } = await axios.get(API_URL_lideres_profit, { headers });
          const alreadyExists = (existingProfits || []).some(
            (p) => p.lider === payload.lider
          );
          if (!alreadyExists) {
            await axios.post(API_URL_lideres_profit, {
              lider: payload.lider,
              utilidad: 0,
            }, { headers });
            console.log(`[Proyecto] Auto-created Profit_lider for "${payload.lider}" with 0%`);
          }
        } catch (profitErr) {
          console.error("[Proyecto] Error auto-creating Profit_lider:", profitErr);
        }
      }

      this.props.resetState();
      this.props.toggle();
    } catch (error) {
      console.error("POST 400 details:", error?.response?.data || error);

      // Handle server-side validation errors
      if (error.response?.data) {
        const serverErrors = error.response.data;
        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          this.setState({ validationErrors: serverErrors, isSubmitting: false });
        } else {
          this.setState({
            errorMessage: 'Error al crear el proyecto. Por favor verifica los datos.',
            isSubmitting: false
          });
        }
      } else {
        this.setState({
          errorMessage: 'Error de conexión. Por favor intenta nuevamente.',
          isSubmitting: false
        });
      }
    }
  };

  // EDIT
  editProyecto = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!this.validateForm()) {
      this.setState({ errorMessage: 'Por favor completa todos los campos obligatorios marcados con *' });
      // Focus first invalid field
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid-custom, .react-select-invalid');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = firstError.querySelector('input, select');
          if (input) input.focus();
        }
      }, 100);
      return;
    }

    const { inicio_date, terminacion_date, pk } = this.state;
    if (inicio_date && terminacion_date && inicio_date > terminacion_date) {
      this.setState({
        errorMessageFecha: "La fecha de terminación debe ser igual o posterior a la fecha de inicio.",
      });
      return;
    }

    this.setState({ isSubmitting: true, errorMessage: '', errorMessageFecha: '' });

    const headers = getAuthHeaders();
    const payload = this.buildProyectoPayload();

    // DRF usualmente requiere slash final
    const url = String(API_URL_proyectos).endsWith("/")
      ? `${API_URL_proyectos}${pk}/`
      : `${API_URL_proyectos}/${pk}/`;

    try {
      await axios.put(url, payload, { headers });
      this.props.resetState();
      this.props.toggle();
    } catch (error) {
      console.error("PUT 400 details:", error?.response?.data || error);

      // Handle server-side validation errors
      if (error.response?.data) {
        const serverErrors = error.response.data;
        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          this.setState({ validationErrors: serverErrors, isSubmitting: false });
        } else {
          this.setState({
            errorMessage: 'Error actualizando el proyecto. Por favor verifica los datos.',
            isSubmitting: false
          });
        }
      } else {
        this.setState({
          errorMessage: 'Error de conexión. Por favor intenta nuevamente.',
          isSubmitting: false
        });
      }
    }
  };

  render() {
    const {
      errorMessage,
      errorMessageFecha,
      inicio_date,
      terminacion_date,
      // lists
      clientesList,
      productosList,
      sedesList,
      // values
      cliente,
      producto,
      status,
      modalidad,
      sede,
      sedeSubcatId,  // <-- NEW
      pais,
      ciudad,
      countries,
      availableCities,
      moneda,
      name,
      lider,
      partnersList,
      validationErrors,
      isSubmitting,
    } = this.state;

    // Búsqueda sin acentos para todos los Select
    const filterOption = createFilter({
      ignoreAccents: true,
      ignoreCase: true,
      trim: true,
      matchFrom: "any",
    });

    // Opciones ordenadas alfabéticamente
    const statusOptions = [
      "Activo",
      "En negociación",
      "En pausa",
      "Terminado",
      "Terminado con Saldo Pendiente",
      "Vencido",
    ]
      .map((s) => ({ value: s, label: s }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const clienteOptions =
      clientesList
        ?.filter((cli) => cli.nombre && cli.nombre.trim().length > 0)
        .map((cli) => ({ value: cli.nombre, label: cli.nombre }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const monedaOptions = ["COP", "EUR", "MXN", "USD"]
      .map((m) => ({ value: m, label: m }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // País: Mexico & United States primero
    const preferred = ["Mexico", "United States"];
    const preferredSet = new Set(preferred.map((n) => n.toLowerCase()));
    const preferredCountries = countries
      .filter((c) => preferredSet.has(c.name.toLowerCase()))
      .map((c) => ({ value: c.name, label: c.name }))
      .sort((a, b) => preferred.indexOf(a.label) - preferred.indexOf(b.label));
    const otherCountries = countries
      .filter((c) => !preferredSet.has(c.name.toLowerCase()))
      .map((c) => ({ value: c.name, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const countryOptions = [...preferredCountries, ...otherCountries];

    const ciudadOptions = availableCities
      .map((c) => ({ value: c.name, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const sedeOptions =
      sedesList
        ?.map((s) => ({ value: s.pk ?? s.id, label: s.sede }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [];

    // ⬇️ subcategorías dependen de la sede seleccionada
    const selectedSede = sedesList.find(
      (s) => String(s.pk ?? s.id) === String(sede || "")
    );
    const subcatOptions = (selectedSede?.subcategories || [])
      .map((sc) => ({
        value: sc.id,
        label: sc.code ? `${sc.name} (${sc.code})` : sc.name,
        isActive: sc.is_active,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return (
      <Form onSubmit={this.props.proyecto ? this.editProyecto : this.createProyecto}>
        {/* Info Note */}
        <p style={{ fontSize: '0.9em', color: '#007bff', marginBottom: '1rem' }}>
          Los campos marcados con <span style={{ color: 'red' }}>*</span> son obligatorios
        </p>

        {/* NOMBRE */}
        <FormGroup className={validationErrors.name ? 'is-invalid-custom' : ''}>
          <Label>Nombre de Proyecto/Curso: <span style={{ color: 'red' }}>*</span></Label>
          <Input
            type="text"
            name="name"
            value={name}
            onChange={this.onChange}
            placeholder="Ingresa el nombre de Proyecto/Curso"
            invalid={!!validationErrors.name}
          />
          {validationErrors.name && (
            <div className="invalid-feedback d-block">{validationErrors.name}</div>
          )}
        </FormGroup>

        {/* STATUS */}
        <FormGroup className={validationErrors.status ? 'react-select-invalid' : ''}>
          <Label>Status: <span style={{ color: 'red' }}>*</span></Label>
          <Select
            options={statusOptions}
            value={statusOptions.find((o) => o.value === status) || null}
            onChange={(opt) => this.setState({ status: opt ? opt.value : "", validationErrors: { ...validationErrors, status: '' } })}
            placeholder="Selecciona Status"
            isClearable
            filterOption={filterOption}
            className={validationErrors.status ? 'is-invalid' : ''}
          />
          {validationErrors.status && (
            <div className="invalid-feedback d-block">{validationErrors.status}</div>
          )}
        </FormGroup>

        {/* CLIENTE */}
        <FormGroup className={validationErrors.cliente ? 'react-select-invalid' : ''}>
          <Label>Cliente:</Label>
          <Select
            options={clienteOptions}
            value={clienteOptions.find((o) => o.value === cliente) || null}
            onChange={(opt) => this.setState({ cliente: opt ? opt.value : "", validationErrors: { ...validationErrors, cliente: '' } })}
            placeholder="Selecciona Cliente"
            isClearable
            filterOption={filterOption}
            className={validationErrors.cliente ? 'is-invalid' : ''}
          />
          {validationErrors.cliente && (
            <div className="invalid-feedback d-block">{validationErrors.cliente}</div>
          )}
        </FormGroup>

        {/* Producto / Modalidad anteriores: ya no se capturan; se muestran solo
            como referencia en proyectos que siguen sin clasificación nueva. */}
        {this.props.proyecto && !this.state.cp_producto && (producto || modalidad) && (
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 10 }}>
            Clasificación anterior de este proyecto: <strong>{producto || '—'}</strong> · <strong>{modalidad || '—'}</strong>. Elige abajo la clasificación nueva equivalente.
          </div>
        )}

        {/* CLASIFICACIÓN DE PRODUCTO (5 niveles, opcional mientras se migra el histórico) */}
        {(() => {
          const { cp_producto, cp_categoria, cp_subproducto, cp_modalidad, cp_tipo } = this.state;
          const productoOpts = this.cpOpciones(this.cpNodos(1, null));
          const categoriaOpts = cp_producto ? this.cpOpciones(this.cpNodos(2, cp_producto)) : [];
          const base = cp_categoria || (cp_producto && categoriaOpts.length === 0 ? cp_producto : null);
          const subOpts = base ? this.cpOpciones(this.cpNodos(3, base)) : [];
          const modOpts = base ? this.cpOpciones(this.cpNodos(4, base)) : [];
          const tipoOpts = base ? this.cpOpciones(this.cpNodos(5, base)) : [];
          if (productoOpts.length === 0) return null;
          const nivel = (label, campo, opts, value, placeholder) => (
            <FormGroup key={campo} className={validationErrors[campo] ? 'react-select-invalid' : ''}>
              <Label>{label}: <span style={{ color: 'red' }}>*</span></Label>
              <Select
                options={opts}
                value={this.cpBuscar(opts, value)}
                onChange={(opt) => this.onChangeCp(campo, opt)}
                placeholder={placeholder}
                isClearable
                isSearchable
                filterOption={filterOption}
                className={validationErrors[campo] ? 'is-invalid' : ''}
              />
              {validationErrors[campo] && (
                <div className="invalid-feedback d-block">{validationErrors[campo]}</div>
              )}
            </FormGroup>
          );
          return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: '1rem', background: '#fafafa' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 6 }}>Clasificación de producto <span style={{ color: 'red' }}>*</span></div>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 10 }}>
                Las opciones de cada nivel dependen del anterior. El catálogo se administra en Operaciones → Clasificación de productos.
              </p>
              {nivel("Producto", "cp_producto", productoOpts, cp_producto, "Selecciona producto")}
              {cp_producto && categoriaOpts.length > 0 && nivel("Categoría", "cp_categoria", categoriaOpts, cp_categoria, "Selecciona categoría")}
              {base && subOpts.length > 0 && nivel("Subproducto", "cp_subproducto", subOpts, cp_subproducto, "Selecciona subproducto")}
              {(() => {
                const sub = cp_subproducto ? (this.state.clasificacionList || []).find((n) => n.pk === cp_subproducto) : null;
                if (!sub || (!sub.duracion_horas && !sub.entregable)) return null;
                return (
                  <p style={{ fontSize: '0.8rem', color: '#374151', marginTop: -6, marginBottom: 10 }}>
                    {sub.duracion_horas ? <strong>{Number(sub.duracion_horas)} h · </strong> : null}
                    {sub.entregable}
                    {sub.notas ? <span style={{ color: '#6b7280' }}> · {sub.notas}</span> : null}
                  </p>
                );
              })()}
              {base && modOpts.length > 0 && nivel("Modalidad", "cp_modalidad", modOpts, cp_modalidad, "Selecciona modalidad")}
              {base && tipoOpts.length > 0 && nivel("Tipo", "cp_tipo", tipoOpts, cp_tipo, "Público o Privado")}
            </div>
          );
        })()}

        {/* FECHAS */}
        <FormGroup>
          <Label>Fecha inicio:</Label>
          <DatePicker
            selected={inicio_date}
            onChange={this.handleDateChange}
            dateFormat="yyyy-MM-dd"
            placeholderText="Selecciona fecha de inicio"
            className="form-control"
          />
        </FormGroup>
        <FormGroup>
          <Label>Fecha terminación:</Label>
          <DatePicker
            selected={terminacion_date}
            onChange={this.handleDateChangeTermina}
            dateFormat="yyyy-MM-dd"
            placeholderText="Selecciona fecha de terminación"
            className="form-control"
          />
        </FormGroup>

        {errorMessageFecha && <Alert color="danger">{errorMessageFecha}</Alert>}

        {/* MONEDA */}
        <FormGroup className={validationErrors.moneda ? 'react-select-invalid' : ''}>
          <Label>Moneda: <span style={{ color: 'red' }}>*</span></Label>
          <Select
            options={monedaOptions}
            value={monedaOptions.find((o) => o.value === moneda) || null}
            onChange={(opt) => this.setState({ moneda: opt ? opt.value : "", validationErrors: { ...validationErrors, moneda: '' } })}
            placeholder="Selecciona Moneda"
            isClearable
            filterOption={filterOption}
            className={validationErrors.moneda ? 'is-invalid' : ''}
          />
          {validationErrors.moneda && (
            <div className="invalid-feedback d-block">{validationErrors.moneda}</div>
          )}
        </FormGroup>

        {/* LÍDER */}
        <FormGroup>
          <Label>Líder:</Label>
          <Select
            options={
              partnersList
                ?.map((p) => ({ value: p.partner, label: p.partner }))
                .sort((a, b) => a.label.localeCompare(b.label)) || []
            }
            value={lider ? { value: lider, label: lider } : null}
            onChange={(opt) => this.setState({ lider: opt ? opt.value : "" })}
            placeholder="Selecciona Líder"
            isClearable
            isSearchable
            filterOption={filterOption}
          />
        </FormGroup>

        {/* ==== UI-ONLY (no se envía a backend) ==== */}
        <FormGroup>
          <Label>Value Stream (UI):</Label>
          <Select
            options={sedeOptions}
            value={sedeOptions.find((o) => String(o.value) === String(sede)) || null}
            onChange={(opt) =>
              this.setState({
                sede: opt ? opt.value : "",
                // reset subcat when switching parent
                sedeSubcatId: "",
              })
            }
            placeholder="Selecciona Value Stream"
            isClearable
            filterOption={filterOption}
          />
        </FormGroup>

        {/* NEW: Subcategoría */}
        <FormGroup>
          <Label>Subcategoría:</Label>
          <Select
            options={subcatOptions}
            value={subcatOptions.find((o) => String(o.value) === String(sedeSubcatId)) || null}
            onChange={(opt) => this.setState({ sedeSubcatId: opt ? opt.value : "" })}
            placeholder={sede ? "Selecciona Subcategoría" : "Selecciona primero un Value Stream"}
            isClearable
            isDisabled={!sede}
            filterOption={createFilter({ ignoreAccents: true, ignoreCase: true, trim: true })}
          />
        </FormGroup>

        <FormGroup>
          <Label>País (UI):</Label>
          <Select
            options={countryOptions}
            value={countryOptions.find((o) => o.value === pais) || null}
            onChange={this.handleChangePaisSelect}
            placeholder="Selecciona un País"
            isClearable
            filterOption={filterOption}
          />
        </FormGroup>

        <FormGroup>
          <Label>Ciudad (UI):</Label>
          <Select
            options={ciudadOptions}
            value={ciudadOptions.find((o) => o.value === ciudad) || null}
            onChange={this.handleChangeCiudadSelect}
            placeholder="Selecciona una Ciudad"
            isClearable
            isDisabled={!pais}
            filterOption={filterOption}
          />
        </FormGroup>
        {/* ==== /UI-ONLY ==== */}

        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

        <Button color="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" /> Guardando...
            </>
          ) : (
            this.props.proyecto ? "Actualizar Proyecto" : "Crear Proyecto"
          )}
        </Button>
      </Form>
    );
  }
}

export default NewProyectoForm;